/**
 * @file repair.ts
 * @description Починка сохранённого JSON-документа по zod-схеме поле за полем
 * (phase19.16 Р2): неверное значение заменяется умолчанием, лишний ключ
 * снимается, негодные элементы списка выбрасываются, длинный список
 * обрезается — и всё это записывается в `replaced`.
 *
 * Зачем: чтение (`readSectionPayload`, `readBlogOptions`) не должно ронять
 * витрину и не должно из-за одного битого поля сбрасывать весь документ
 * (как `readSiteTheme` сбрасывает только битую часть, здесь — только битое
 * поле). Строгая проверка для записи — отдельно (validate). Внутренний модуль
 * пакета: в барель не экспортируется.
 */

import { z } from "zod";

/** Что заменено при чтении: путь и причина словами. */
export interface RepairIssue {
  path: string;
  reason: string;
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** Копия JSON-значения (умолчания бывают заморожены). */
export function cloneJson<T>(value: T): T {
  return value === undefined ? value : (JSON.parse(JSON.stringify(value)) as T);
}

const join = (path: string, key: string | number): string => (path ? `${path}.${key}` : String(key));

/** Снимает обёртки optional / nullable — для решения «объект или список ли внутри». */
function unwrap(schema: z.ZodType): z.ZodType {
  let current: z.ZodType = schema;
  for (;;) {
    if (current instanceof z.ZodOptional || current instanceof z.ZodNullable) {
      current = current.unwrap() as z.ZodType;
      continue;
    }
    return current;
  }
}

/** Наибольшая длина списка из ошибки схемы (если ошибка — «слишком много»). */
function arrayMaximum(schema: z.ZodType, value: unknown[]): number | null {
  const parsed = schema.safeParse(value);
  if (parsed.success) return null;
  const tooBig = parsed.error.issues.find((issue) => issue.code === "too_big" && issue.path.length === 0);
  return tooBig && tooBig.code === "too_big" ? Number(tooBig.maximum) : null;
}

/**
 * Починка значения `value` по схеме `schema` с умолчанием `fallback`.
 * Возвращает исправленное значение; если исправить нельзя и умолчания нет —
 * возвращает `value` как есть (решает вызывающий финальной проверкой).
 */
export function repairBySchema(
  schema: z.ZodType,
  value: unknown,
  fallback: unknown,
  path: string,
  replaced: RepairIssue[],
): unknown {
  if (schema.safeParse(value).success) return value;
  const inner = unwrap(schema);

  if (inner instanceof z.ZodObject && isPlainObject(value)) {
    const shape = inner.shape as Record<string, z.ZodType>;
    const defaults = isPlainObject(fallback) ? fallback : {};
    const result: Record<string, unknown> = {};
    for (const key of Object.keys(value)) {
      if (!(key in shape)) replaced.push({ path: join(path, key), reason: "Лишнее поле снято" });
    }
    for (const [key, field] of Object.entries(shape)) {
      const fixed = repairBySchema(field, value[key], defaults[key], join(path, key), replaced);
      if (fixed !== undefined) result[key] = fixed;
    }
    return result;
  }

  if (inner instanceof z.ZodArray && Array.isArray(value)) {
    const element = inner.element as z.ZodType;
    const kept: unknown[] = [];
    value.forEach((item, index) => {
      const fixed = repairBySchema(element, item, undefined, join(path, index), replaced);
      if (element.safeParse(fixed).success) kept.push(fixed);
      else replaced.push({ path: join(path, index), reason: "Элемент списка не прошёл проверку — снят" });
    });
    const max = arrayMaximum(inner, kept);
    const list = max !== null ? kept.slice(0, max) : kept;
    if (max !== null) replaced.push({ path, reason: `Список длиннее ${max} — лишнее снято` });
    if (inner.safeParse(list).success) return list;
  }

  if (fallback !== undefined) {
    if (value !== undefined) replaced.push({ path, reason: "Значение не прошло проверку — взято умолчание" });
    return cloneJson(fallback);
  }
  if (schema.safeParse(undefined).success) {
    if (value !== undefined) replaced.push({ path, reason: "Значение не прошло проверку — снято" });
    return undefined;
  }
  return value;
}
